import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Input, Label } from "reactstrap";
import { AppointmentService } from "../services/AppointmentService";
import moment from "moment";
import { toast } from "react-toastify";
import { environment } from "../environment/environment";

interface ConfirmAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  therapistName: string;
  therapistId: string;
  clientId: string;
  date: string; // e.g. "Friday, June 6, 2025"
  time: string; // e.g. "9:30 AM"
  selectedDateTime: Date; // Full date object for the selected date and time
}

const ConfirmAppointmentModal: React.FC<ConfirmAppointmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  therapistName,
  therapistId,
  clientId,
  date,
  time,
  selectedDateTime,
}) => {
  const [isCreatingAppointment, setIsCreatingAppointment] = useState(false);
  const [agreedToDisclosure, setAgreedToDisclosure] = useState(false);
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [agreedToConsent, setAgreedToConsent] = useState(false);

  // Debug logs
  // console.log('Current checkbox states:', { agreedToPolicy, agreedToConsent });

  const handleConfirm = async () => {
    setIsCreatingAppointment(true);

    try {
      // Create start and end times
      const startTime = moment(selectedDateTime);
      const endTime = moment(selectedDateTime).add(1, 'hour');

      // Create appointment data
      const appointmentData = {
        therapistId: therapistId,
        clientId: clientId,
        title: "Lavni Therapy session",
        type: "Video - 1 Hour Session",
        start: startTime.toISOString(),
        end: endTime.toISOString(),
        reminders: [30],
        repeatInfo: {
          repeatType: "DOES NOT REPEAT",
          interval: "",
          repeatDays: {
            sunday: false,
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false
          },
          endingDate: endTime.toISOString(),
          endingAfter: 10,
          endingType: ""
        },
        color: "#FF6900",
        groupId: `${therapistId}_${startTime.valueOf()}`,
        eventOnHolidays: false
      };
      console.log({appointmentData});

      // Call API to create appointment
      const response = await AppointmentService.createAppointmentByClient(appointmentData);

      if (response.success) {
        toast.success("Appointment scheduled successfully!");
        onConfirm();
      } else {
        toast.error(response.message || "Could not schedule appointment. Please try again.");
      }
    } catch (error) {
      console.error("Error creating appointment:", error);
      toast.error("An error occurred while scheduling the appointment. Please try again.");
    } finally {
      setIsCreatingAppointment(false);
    }
  };
  return (
    <Modal isOpen={isOpen} toggle={onClose} centered style={{ borderRadius: 16 }}>
      <ModalHeader toggle={onClose} style={{ border: "none", fontWeight: 700 }}>
        Confirm Appointment
      </ModalHeader>
      <ModalBody style={{ textAlign: "center", padding: "24px 24px 0 24px" }}>
        <div style={{ color: "#5D6A7E", fontSize: 16, marginBottom: 20 }}>
          You are about to schedule an appointment<br />
          with <b>{therapistName}</b>.
        </div>
        <div style={{
          background: "#FFF7EE",
          borderRadius: 12,
          padding: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 24
        }}>
          <div style={{
            background: "#FFE1C7",
            borderRadius: "50%",
            width: 44,
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 16,
          }}>
            <i className="bx bx-calendar" style={{ color: "#FF8F2D", fontSize: 24 }}></i>
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontWeight: 600, color: "#23272E", fontSize: 17 }}>{date}</div>
            <div style={{ color: "#23272E", fontSize: 16, marginTop: 2 }}>{time}</div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter style={{ border: "none", flexDirection: "column", gap: 12 }}>
        <div style={{ width: "100%", marginBottom: 16, textAlign: "left" }}>
          {/* Custom checkbox #1 - Disclosure Statement */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 12,
              cursor: "pointer",
            }}
            onClick={() => {
              console.log('Disclosure checkbox clicked, old value:', agreedToDisclosure);
              setAgreedToDisclosure(!agreedToDisclosure);
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                border: "1px solid #ccc",
                borderRadius: 4,
                marginRight: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: agreedToDisclosure ? "#FF8F2D" : "white"
              }}
            >
              {agreedToDisclosure && <i className="bx bx-check" style={{ color: "white", fontSize: 16 }}></i>}
            </div>
            <div style={{ color: "#5D6A7E", fontSize: 14 }}>
              I have read the <span
                style={{ color: "#FF8F2D", marginLeft: 4, marginRight: 4, cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  const disclosureUrl = `${environment.api_url}/api/public/disclosure-statement-of-therapist/${therapistId}`;
                  window.open(disclosureUrl, '_blank');
                }}
              >disclosure statement</span> & Accept.
            </div>
          </div>

          {/* Custom checkbox #2 - No Show Policy */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 12,
              cursor: "pointer",
            }}
            onClick={() => {
              console.log('Policy checkbox clicked, old value:', agreedToPolicy);
              setAgreedToPolicy(!agreedToPolicy);
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                border: "1px solid #ccc",
                borderRadius: 4,
                marginRight: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: agreedToPolicy ? "#FF8F2D" : "white"
              }}
            >
              {agreedToPolicy && <i className="bx bx-check" style={{ color: "white", fontSize: 16 }}></i>}
            </div>
            <div style={{ color: "#5D6A7E", fontSize: 14 }}>
              I agree to <span 
                style={{ color: "#FF8F2D", marginLeft: 4, marginRight: 4, cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent toggling checkbox when clicking the link
                  window.open("/noshow-policy/", "_blank");
                }}
              >No Show and Cancellation Policy</span> of Lavni Inc.
            </div>
          </div>

          {/* Custom checkbox #3 - Consent Document */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => {
              console.log('Consent checkbox clicked, old value:', agreedToConsent);
              setAgreedToConsent(!agreedToConsent);
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                border: "1px solid #ccc",
                borderRadius: 4,
                marginRight: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: agreedToConsent ? "#FF8F2D" : "white"
              }}
            >
              {agreedToConsent && <i className="bx bx-check" style={{ color: "white", fontSize: 16 }}></i>}
            </div>
            <div style={{ color: "#5D6A7E", fontSize: 14 }}>
              I agree to <span 
                style={{ color: "#FF8F2D", marginLeft: 4, marginRight: 4, cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent toggling checkbox when clicking the link
                  window.open("/consent-document/", "_blank");
                }}
              >Consent Document</span> of Lavni Inc.
            </div>
          </div>
        </div>
        <Button
          color=""
          style={{
            background: (agreedToDisclosure && agreedToPolicy && agreedToConsent) ? "#FF8F2D" : "#cccccc",
            color: "white",
            fontWeight: 600,
            fontSize: 17,
            borderRadius: 8,
            width: "100%",
            boxShadow: (agreedToDisclosure && agreedToPolicy && agreedToConsent) ? "0 2px 8px rgba(255,143,45,0.18)" : "none",
            border: (agreedToDisclosure && agreedToPolicy && agreedToConsent) ? "2px solid #FF8F2D" : "2px solid #cccccc",
            opacity: isCreatingAppointment ? 0.7 : 1,
            cursor: (isCreatingAppointment || !agreedToDisclosure || !agreedToPolicy || !agreedToConsent) ? "not-allowed" : "pointer"
          }}
          onClick={() => {
            console.log('Confirm button clicked, checkbox states:', { agreedToDisclosure, agreedToPolicy, agreedToConsent });
            handleConfirm();
          }}
          disabled={isCreatingAppointment || !agreedToDisclosure || !agreedToPolicy || !agreedToConsent}
        >
          {isCreatingAppointment ? "Scheduling..." : "Confirm Appointment"}
        </Button>
        <Button
          color="link"
          style={{ color: "#23272E", fontWeight: 500, fontSize: 16, width: "100%" }}
          onClick={onClose}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmAppointmentModal;
